import React from "react";
import { HeaderTipo, Lto, PlantillaBoton } from "../../../../services/v2/plantillas.service";

/** *negrita*, _cursiva_, ~tachado~ y ```mono``` de WhatsApp, y los datos del cliente resaltados. */
export const formatoWhatsApp = (texto: string, valores: Record<string, string>, resaltar: boolean): React.ReactNode[] => {
	const out: React.ReactNode[] = [];
	// Primero los datos {{nombre}}: se ven con el ejemplo (o el valor real) y, en el editor, resaltados.
	const partes = texto.split(/(\{\{\s*[a-z_]+\s*\}\})/g);
	partes.forEach((parte, i) => {
		const m = parte.match(/^\{\{\s*([a-z_]+)\s*\}\}$/);
		if (m) {
			const v = valores[m[1]];
			out.push(<span key={"v" + i} className={resaltar ? "pl-dato" : undefined} title={resaltar ? m[1] : undefined}>{v || "{{" + m[1] + "}}"}</span>);
			return;
		}
		const re = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|```[^`]+```)/g;
		let ultimo = 0;
		let x: RegExpExecArray | null;
		while ((x = re.exec(parte))) {
			if (x.index > ultimo) out.push(parte.slice(ultimo, x.index));
			const t = x[0];
			if (t.startsWith("```")) out.push(<code key={"c" + i + x.index}>{t.slice(3, -3)}</code>);
			else if (t[0] === "*") out.push(<b key={"b" + i + x.index}>{t.slice(1, -1)}</b>);
			else if (t[0] === "_") out.push(<i key={"i" + i + x.index}>{t.slice(1, -1)}</i>);
			else out.push(<s key={"s" + i + x.index}>{t.slice(1, -1)}</s>);
			ultimo = x.index + t.length;
		}
		if (ultimo < parte.length) out.push(parte.slice(ultimo));
	});
	return out;
};

export interface VistaPreviaProps {
	headerTipo: HeaderTipo;
	headerTexto?: string | null;
	mediaUrl?: string | null;
	mediaNombre?: string | null;
	cuerpo: string;
	pie?: string | null;
	botones: PlantillaBoton[];
	valores: Record<string, string>;
	resaltar?: boolean;
	lto?: Lto | null;
}

const IcoBoton: React.FC<{ tipo: string }> = ({ tipo }) => {
	if (tipo === "url") return <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
	if (tipo === "phone") return <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>;
	if (tipo === "copy") return <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" fill="none" stroke="currentColor" strokeWidth="2" /></svg>;
	return <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
};

/**
 * Cómo la ve el cliente en WhatsApp: fondo del chat, burbuja blanca, encabezado (foto, video, PDF o título), texto con
 * su formato, pie gris, hora y los botones debajo, separados, como los pinta WhatsApp.
 */
const VistaPrevia: React.FC<VistaPreviaProps> = ({ headerTipo, headerTexto, mediaUrl, mediaNombre, cuerpo, pie, botones, valores, resaltar, lto }) => {
	const hora = new Date().toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit" }).replace(/\s?([ap])\.?\s?m\.?/i, " $1m");
	const textoBoton = (b: PlantillaBoton) => b.tipo === "copy" ? "Copiar código" : (b.texto || "Botón");
	return (
		<div className="pl-telefono" aria-label="Vista previa en WhatsApp">
			<div className="pl-telefono-barra"><span className="pl-avatar">W</span><span><b>Washington Academy</b><small>cuenta de empresa</small></span></div>
			<div className="pl-chat">
				<div className="pl-burbuja">
					{headerTipo === "image" ? (mediaUrl ? <img className="pl-media" src={mediaUrl} alt="" /> : <div className="pl-media vacia">Foto</div>) : null}
					{headerTipo === "video" ? (mediaUrl ? <video className="pl-media" src={mediaUrl} controls playsInline /> : <div className="pl-media vacia">Video</div>) : null}
					{headerTipo === "document" ? (
						<div className="pl-doc"><span className="pl-doc-ico">PDF</span><span>{mediaNombre || "documento.pdf"}</span></div>
					) : null}
					{headerTipo === "text" && headerTexto ? <div className="pl-titulo">{formatoWhatsApp(headerTexto, valores, !!resaltar)}</div> : null}
					{/* Oferta por tiempo limitado: WhatsApp pinta el reloj y lo que falta, debajo del encabezado. */}
					{lto ? (
						<div className="pl-lto">
							<span className="pl-lto-ico" aria-hidden="true">⏰</span>
							<span><b>{lto.titulo || "Título de la oferta"}</b><small>Termina en {lto.horas >= 48 ? Math.round(lto.horas / 24) + " días" : lto.horas + " horas"}</small></span>
						</div>
					) : null}
					<div className="pl-cuerpo">{cuerpo.trim() ? formatoWhatsApp(cuerpo, valores, !!resaltar) : <span className="pl-vacio-txt">Escribe el mensaje…</span>}</div>
					{pie ? <div className="pl-pie">{pie}</div> : null}
					<div className="pl-hora">{hora}</div>
				</div>
				{botones.length > 0 ? (
					<div className="pl-botones">
						{botones.slice(0, botones.length > 3 ? 2 : 3).map((b, i) => (
							<div key={i} className="pl-boton"><IcoBoton tipo={b.tipo} />{textoBoton(b)}</div>
						))}
						{/* Con más de 3, WhatsApp enseña 2 y "Ver todas las opciones". */}
						{botones.length > 3 ? <div className="pl-boton"><IcoBoton tipo="lista" />Ver todas las opciones</div> : null}
					</div>
				) : null}
			</div>
		</div>
	);
};

export default VistaPrevia;
